import { Inject, Injectable, NgZone, OnDestroy } from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  throwError,
  Subject,
  filter,
  takeUntil,
} from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { EvaluationService } from "./evaluation.service";
import { AppConfigService } from "src/app/services/app-config.service";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { UserService } from "./user.service";
import { HttpClient } from "@angular/common/http";
import { catchError, debounceTime, map, switchMap } from "rxjs/operators";
import { RetryService } from "./retry.service";
import {
  CourseSearchResponse,
  CourseSearchResponseSchema,
  PreEvaluationReportResponse,
  PreEvaluationReportResponseSchema,
} from "./equivalency.schemas";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";

@Injectable({
  providedIn: "root",
})
export class EquivalencyService implements OnDestroy {
  private readonly showConsoleLogs: boolean = false;
  private readonly equivalencyWebsocketUrl: string = "";
  private readonly finalGraphClosingWebsocketUrl: string = "";
  private readonly searchSubject = new Subject<string>();
  private readonly courseSearchApiUrl: string = "";
  private readonly preEvaluationReportWebsocketUrl: string = "";

  //equivalency data
  private readonly equivalencyDataSubject = new BehaviorSubject<any>(null);
  equivalencyData$ = this.equivalencyDataSubject.asObservable();

  private readonly equivalencyMap = new Map<string, any>();
  private readonly equivalencyMapSubject = new BehaviorSubject<any[]>([]);
  equivalencyResult$ = this.equivalencyMapSubject.asObservable();

  private readonly equivalencySummarySubject = new BehaviorSubject<any>(null);
  equivalencySummary$ = this.equivalencySummarySubject.asObservable();

  private readonly categorySummariesSubject = new BehaviorSubject<any[] | null>(
    null,
  );
  categorySummaries$ = this.categorySummariesSubject.asObservable();

  private readonly validationErrorSubject = new Subject<string>();
  validationError$ = this.validationErrorSubject.asObservable();

  private readonly graphClosingStatusSubject = new BehaviorSubject<boolean>(
    false,
  );
  graphClosingStatus$ = this.graphClosingStatusSubject.asObservable();

  private readonly reportCompletedSubject = new BehaviorSubject<boolean>(false);
  reportCompleted$ = this.reportCompletedSubject.asObservable();

  private readonly reportFailedSubject = new BehaviorSubject<boolean>(false);
  reportFailed$ = this.reportFailedSubject.asObservable();

  private readonly reportGeneratingSubject = new BehaviorSubject<boolean>(
    false,
  );
  reportGenerating$ = this.reportGeneratingSubject.asObservable();

  private socket?: WebSocket;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly evaluationService: EvaluationService,
    private readonly envService: AppConfigService,
    private readonly zone: NgZone,
    private readonly userService: UserService,
    private readonly http: HttpClient,
    private readonly retryService: RetryService,
    private readonly snackBar: MatSnackBar,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
    this.equivalencyWebsocketUrl = this.envService.mmpEquivalencyWebsocketUrl;
    this.finalGraphClosingWebsocketUrl =
      this.envService.mmpGraphClosingWebsocketUrl;
    this.courseSearchApiUrl = this.envService.mmpCourseSearchApiUrl;
    this.preEvaluationReportWebsocketUrl =
      this.envService.mmpPreEvaluationReportUrl;
    this.getRetryResults();
  }

  startWebSocket() {
    const transcripts = this.evaluationService.getEvaluationMapValues();
    if (this.showConsoleLogs) {
      console.log("Transcripts from EvaluationService:", transcripts);
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
    this.socket = new WebSocket(this.equivalencyWebsocketUrl);

    this.socket.onopen = async () => {
      const accessToken = this.oktaAuth.getAccessToken();

      this.socket.send(
        JSON.stringify({
          type: "auth",
          token: accessToken,
        }),
      );

      const payload = [];

      for (const transcript of transcripts) {
        const transcriptEach = {
          session_key: transcript.sessionKey,
          pre_evaluation_checkpoint_id: transcript.checkpoint,
          pre_evaluation_changed_data: transcript.output,
          proceed_to_next_stage: transcript.eligibleForProceeding,
        };
        payload.push(transcriptEach);
      }

      const transcriptData = JSON.stringify(payload);
      this.socket.send(transcriptData);

      const intervalId = setInterval(() => {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({
              type: MESSAGES.WEBSOCKET.HANDSHAKE_TYPE,
              text: MESSAGES.WEBSOCKET.UPLOAD_HANDSHAKE_MESSAGE,
            }),
          ); //Handshake senting to backend(server)
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.FIXED_MESSAGE_SENT);
          }
        } else {
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.HANDSHAKE_STOPED);
          }

          clearInterval(intervalId);
        }
      }, 10000);
    };
    this.socket.onmessage = (event) => {
      // Ensure Angular change detection runs
      this.zone.run(() => this.handleMessage(event));
    };
    this.socket.onerror = (error) => {
      if (this.showConsoleLogs) {
        console.error("WebSocket error:", error);
      }
    };

    this.socket.onclose = (event) => {
      const currentValue = this.getEquivalencyMapValues();
      if (event.wasClean) {
        this.handleCleanClose(event, currentValue);
      } else {
        this.handleUnexpectedClose(event, currentValue);
      }
    };
  }

  private handleCleanClose(event: CloseEvent, currentValue: any[]): void {
    if (this.showConsoleLogs) {
      console.log(MESSAGES.WEBSOCKET.CLOSED_CLEAN, event.code, event.reason);
    }

    if (
      ComparisonNormalizer.string(event.reason) === "keepalive ping timeout"
    ) {
      this.retryService.checkRetryRequired(currentValue, "course_matching");
    }
  }

  private handleUnexpectedClose(event: CloseEvent, currentValue: any[]): void {
    if (this.showConsoleLogs) {
      console.warn(
        MESSAGES.WEBSOCKET.CLOSED_UNEXPECTED,
        event.code,
        event.reason,
      );
    }

    if (currentValue.length === 0) {
      if (this.showConsoleLogs) {
        console.log("Websocket closed unexpectedly, retry upload ws");
      }
      this.equivalencyRetry();
    } else {
      this.retryService.checkRetryRequired(currentValue, "course_matching");
    }
  }

  private handleMessage(event: MessageEvent) {
    const data = JSON.parse(event.data);
    const sessionKey = data.session_key;

    this.processResumeStarted(data.status, sessionKey);
    this.confirmEquivalencyMapEntry(sessionKey);
    this.processCourseMatchingOutput(data, sessionKey);
    this.processCourseMatchingCheckpoint(data, sessionKey);
    this.processCreditBreakdown(data);
    this.processFinalSummary(data);
    this.handleCompletedStatus(data.status);
  }

  public processCreditBreakdown(data: any): void {
    if (ComparisonNormalizer.string(data.type) !== "credit_breakdown") {
      return;
    }

    const summaries = Array.isArray(data.data) ? data.data : [];
    this.categorySummariesSubject.next(summaries);
  }

  public processResumeStarted(status: string, sessionKey: string): void {
    if (ComparisonNormalizer.string(status) === "resume_started") {
      if (!this.equivalencyMap.has(sessionKey)) {
        this.equivalencyMap.set(sessionKey, {
          sessionKey,
          status: "started",
          output: "",
          checkpoint: "",
          isPending: true,
        });
      }
    }
  }

  public initializeEquivalencyMapPending(sessionKey: string): void {
    if (!this.equivalencyMap.has(sessionKey)) {
      this.equivalencyMap.set(sessionKey, {
        sessionKey,
        status: "pending",
        output: "",
        checkpoint: "",
        isPending: true,
      });
    }
  }

  public confirmEquivalencyMapEntry(sessionKey: string): void {
    const transcript = this.equivalencyMap.get(sessionKey);
    if (transcript?.isPending) {
      transcript.status = "started";
      delete transcript.isPending;
      this.emit();
    }
  }

  public resolvePendingEquivalencyMapEntry(sessionKey: string): void {
    const transcript = this.equivalencyMap.get(sessionKey);
    if (!transcript?.isPending) {
      return;
    }

    this.equivalencyMap.delete(sessionKey);
    this.emit();
  }

  public processCourseMatchingOutput(data: any, sessionKey: string): void {
    if (ComparisonNormalizer.string(data.node) !== "course_matching_outputs") {
      return;
    }

    const transcript = this.equivalencyMap.get(sessionKey);
    if (transcript) {
      transcript.status = "completed";
      transcript.output = data.output;
    }
    this.emit();
  }

  public processCourseMatchingCheckpoint(data: any, sessionKey: string): void {
    if (
      ComparisonNormalizer.string(data.checkpoint_type) !==
      "course_matching_checkpoint"
    ) {
      return;
    }

    const transcript = this.equivalencyMap.get(sessionKey);
    if (transcript) {
      transcript.checkpoint = data.course_matching_checkpoint_id;
    }
  }

  public processFinalSummary(data: any): void {
    if (ComparisonNormalizer.string(data.type) === "final_summary") {
      this.equivalencySummarySubject.next(data.data);
    }
  }

  private handleCompletedStatus(status: string): void {
    if (ComparisonNormalizer.string(status) !== "completed") {
      return;
    }

    const allCompleted = Array.from(this.equivalencyMap.values()).every(
      (transcript) =>
        ComparisonNormalizer.string(transcript.status) === "completed",
    );

    if (allCompleted) {
      if (this.showConsoleLogs) {
        console.log("All transcripts are completed. Closing WebSocket...");
      }
      this.disconnect();
      return;
    }

    if (this.showConsoleLogs) {
      console.log(MESSAGES.RETRY.RETRY_REQUIRED);
    }
    this.disconnect();
    const currentValue = this.getEquivalencyMapValues();
    this.retryService.checkRetryRequired(currentValue, "course_matching");
  }

  private emit() {
    this.equivalencyMapSubject.next(
      Array.from(this.equivalencyMap.values()).filter(
        (entry: any) => !entry.isPending,
      ),
    );
  }

  disconnect() {
    this.socket?.close();
  }

  setEquivalencyData(data: any) {
    this.equivalencyDataSubject.next(data);
  }

  getEquivalencyData() {
    return this.equivalencyDataSubject.value;
  }

  resetGraphClosingStatus(): void {
    this.graphClosingStatusSubject.next(false);
  }

  clearEquivalencyResult() {
    this.equivalencyMap.clear();
    this.equivalencyMapSubject.next([]);
    this.equivalencyDataSubject.next(null);
    this.equivalencySummarySubject.next(null);
    this.categorySummariesSubject.next(null);
    this.graphClosingStatusSubject.next(false);
  }

  getEquivalencyMapValues(): any {
    return Array.from(this.equivalencyMap.values()).filter(
      (entry: any) => !entry.isPending,
    );
  }

  updateEquivalencyOutput(sessionKey: string, newOutput: string): void {
    // Check if the sessionKey exists in the evaluationMap
    const transcript = this.equivalencyMap.get(sessionKey);
    if (!transcript) {
      if (this.showConsoleLogs) {
        console.error(`Transcript with sessionKey ${sessionKey} not found.`);
      }
      return;
    }

    // Update the output of the transcript
    transcript.output.result.credit_summary = newOutput;
  }

  graphClosing() {
    this.resetGraphClosingStatus();

    const transcripts = this.getEquivalencyMapValues();
    if (this.showConsoleLogs) {
      console.log("Transcripts from EquivalencyService:", transcripts);
    }
    const currentUser = this.userService.getUser();
    this.socket = new WebSocket(this.finalGraphClosingWebsocketUrl);
    this.socket.onopen = async () => {
      const accessToken = this.oktaAuth.getAccessToken();
      this.socket.send(
        JSON.stringify({
          type: "auth",
          token: accessToken,
        }),
      );

      const payload = [];

      for (const transcript of transcripts) {
        const transcriptEach = {
          session_key: transcript.sessionKey,
          course_matching_checkpoint_id: transcript.checkpoint,
          course_matching_data_changed: transcript.output,
          current_user_email: currentUser.email,
        };
        payload.push(transcriptEach);
      }

      const transcriptData = JSON.stringify(payload);
      this.socket.send(transcriptData);
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (this.showConsoleLogs) {
          console.log(MESSAGES.WEBSOCKET.BACKEND_MESSAGE, data);
        }

        if (ComparisonNormalizer.string(data.status) === "completed") {
          this.disconnect();
          if (this.showConsoleLogs) {
            console.log("course matching initated graph close");
          }
          this.graphClosingStatusSubject.next(true);
        }
      } catch (error) {
        if (this.showConsoleLogs) {
          console.error(MESSAGES.WEBSOCKET.ONMESSAGE_ERROR, error);
        }
      }
    };
  }

  getPreEvaluationReport(
    sessionKeys: string[],
  ): Observable<PreEvaluationReportResponse> {
    const wsUrl = this.preEvaluationReportWebsocketUrl;
    const userName = this.userService.getUser().name;

    if (this.showConsoleLogs) {
      console.log(
        "Opening pre-evaluation report websocket for sessionKeys:",
        sessionKeys,
      );
    }

    return new Observable<PreEvaluationReportResponse>((observer) => {
      const socket = new WebSocket(wsUrl);
      let handshakeIntervalId:
        | ReturnType<typeof globalThis.setInterval>
        | undefined;
      let completed = false;

      socket.onopen = () => {
        const accessToken = this.oktaAuth.getAccessToken();

        socket.send(
          JSON.stringify({
            type: "auth",
            token: accessToken,
          }),
        );

        const payload = {
          session_keys: sessionKeys,
          user_name: userName,
        };

        socket.send(JSON.stringify(payload));

        // Keep the handshake message as used in other websockets
        handshakeIntervalId = globalThis.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(
              JSON.stringify({
                type: MESSAGES.WEBSOCKET.HANDSHAKE_TYPE,
                text: MESSAGES.WEBSOCKET.UPLOAD_HANDSHAKE_MESSAGE,
              }),
            );
            if (this.showConsoleLogs) {
              console.log(MESSAGES.WEBSOCKET.FIXED_MESSAGE_SENT);
            }
          } else {
            if (this.showConsoleLogs) {
              console.log(MESSAGES.WEBSOCKET.HANDSHAKE_STOPED);
            }
            if (handshakeIntervalId !== undefined) {
              clearInterval(handshakeIntervalId);
            }
          }
        }, 10000);
      };

      socket.onmessage = (event) => {
        this.zone.run(() => {
          try {
            const data = JSON.parse(event.data);

            if (this.showConsoleLogs) {
              console.log(
                MESSAGES.WEBSOCKET.BACKEND_MESSAGE,
                "pre-evaluation report:",
                data,
              );
            }

            // Expect:
            // {"status": "success", "blob_url": "..."}
            // then
            // {"status": "completed"}
            const status = ComparisonNormalizer.string(data.status);

            if (status === "success") {
              // Validate with existing schema
              try {
                const parsed = PreEvaluationReportResponseSchema.parse(data);
                observer.next(parsed);
              } catch (parseError) {
                if (this.showConsoleLogs) {
                  console.error(
                    "Pre-evaluation response validation failed (schema mismatch):",
                    parseError,
                  );
                }
                observer.error(
                  new Error(
                    "Invalid pre-evaluation report response payload (schema mismatch)",
                  ),
                );
              }
            } else if (status === "completed") {
              completed = true;
              observer.complete();
              socket.close();
            } else if (status === "error") {
              // Optional: handle error payload shape from backend
              const message =
                data.message || "Pre-evaluation report generation failed";
              if (this.showConsoleLogs) {
                console.error("Pre-evaluation report error:", data);
              }
              observer.error(new Error(message));
              socket.close();
            }
          } catch (error) {
            if (this.showConsoleLogs) {
              console.error(MESSAGES.WEBSOCKET.ONMESSAGE_ERROR, error);
            }
            observer.error(error);
            socket.close();
          }
        });
      };

      socket.onerror = (event) => {
        if (this.showConsoleLogs) {
          console.error("Pre-evaluation report websocket error:", event);
        }
        this.zone.run(() => {
          observer.error(new Error("Pre-evaluation report websocket error"));
        });
      };

      socket.onclose = (event) => {
        if (handshakeIntervalId !== undefined) {
          clearInterval(handshakeIntervalId);
        }

        if (this.showConsoleLogs) {
          console.log(
            "Pre-evaluation report websocket closed:",
            event.code,
            event.reason,
          );
        }

        // If server closed before we marked completed, treat as error
        if (!completed && event.code !== 1000) {
          this.zone.run(() => {
            observer.error(
              new Error(
                `Pre-evaluation report websocket closed unexpectedly (${event.code})`,
              ),
            );
          });
        }
      };

      // Teardown: called when subscriber unsubscribes
      return () => {
        if (handshakeIntervalId !== undefined) {
          clearInterval(handshakeIntervalId);
        }
        if (socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      };
    }).pipe(
      catchError((error) => {
        if (this.showConsoleLogs) {
          console.error("Error in pre-evaluation report websocket: ", error);
        }
        return throwError(
          () => new Error("Failed to fetch pre-evaluation report (websocket)"),
        );
      }),
    );
  }

  // Expose the search observable
  getSearchResults(): Observable<any[]> {
    return this.searchSubject.pipe(
      debounceTime(300), // Wait for 300ms after the last keystroke
      //distinctUntilChanged(), // Ignore duplicate search keys
      switchMap((searchKey) => this.fetchCourses(searchKey)),
    );
  }

  // Trigger the search
  searchCourses(searchKey: string): void {
    this.searchSubject.next(searchKey);
  }

  // Fetch courses from the API
  private fetchCourses(searchKey: string): Observable<any[]> {
    return this.http
      .get<any>(
        `${this.courseSearchApiUrl}?search_key=${encodeURIComponent(searchKey)}`,
      )
      .pipe(
        map((result) => {
          // Validate the full response using Zod
          try {
            const parsed: CourseSearchResponse =
              CourseSearchResponseSchema.parse(result);
            return parsed.courses;
          } catch (parseError) {
            if (this.showConsoleLogs) {
              console.error(
                "Course search response validation failed (schema mismatch):",
                parseError,
              );
            }
            throw new Error(
              "Invalid course search response payload (schema mismatch)",
            );
          }
        }),
        catchError((error) => {
          if (this.showConsoleLogs) {
            console.error("Error fetching courses:", error);
          }
          return throwError(() => new Error("Failed to fetch courses"));
        }),
      );
  }

  getRetryResults() {
    this.retryService.retryResult$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (result) =>
            ComparisonNormalizer.string(result.type) === "course_matching",
        ),
      )
      .subscribe((result) => {
        const data = result.payload;
        const sessionKey = data.session_key;

        if (
          ComparisonNormalizer.string(data.node) === "course_matching_outputs"
        ) {
          const transcript = this.equivalencyMap.get(sessionKey);
          if (transcript) {
            transcript.status = "completed";
            transcript.output = data.output;
          }
          this.emit();
        }

        if (
          ComparisonNormalizer.string(data.checkpoint_type) ===
          "course_matching_checkpoint"
        ) {
          const transcript = this.equivalencyMap.get(sessionKey);
          if (transcript) {
            transcript.checkpoint = data.pre_evaluation_checkpoint_id;
          }
        }

        if (ComparisonNormalizer.string(data.type) === "final_summary") {
          this.equivalencySummarySubject.next(data.data);
        }
      });
  }

  equivalencyRetry() {
    this.retryService.showAgentRetryConfirmationDialog().then((response) => {
      if (ComparisonNormalizer.boolean(response) === true) {
        this.startWebSocket();
      }
    });
  }

  disconnectRetry(): void {
    this.retryService.disconnect();
  }

  setReportCompleted(completed: boolean): void {
    this.reportCompletedSubject.next(completed);
  }

  setReportFailed(failed: boolean): void {
    this.reportFailedSubject.next(failed);
  }

  setReportGenerating(generating: boolean): void {
    this.reportGeneratingSubject.next(generating);
  }

  resetReportState(): void {
    this.reportCompletedSubject.next(false);
    this.reportFailedSubject.next(false);
    this.reportGeneratingSubject.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
